import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Warning as WarningIcon,
  LocalHospital as HospitalIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import axiosInstance from '../config/axios';

// Componente para la sección de detalles
const DetailSection = ({ title, content, icon }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      {icon}
      <Typography variant="h6" sx={{ ml: 1 }}>
        {title}
      </Typography>
    </Box>
    <Typography variant="body1" sx={{ ml: 4 }}>
      {Array.isArray(content) ? (
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {content.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      ) : (
        content
      )}
    </Typography>
  </Box>
);

const getUrgencyColor = (nivelUrgencia) => {
  const colores = {
    'ESI-1': '#FF4B4B', // Rojo
    'ESI-2': '#FF8F3E', // Naranja
    'ESI-3': '#FFB302', // Amarillo
    'ESI-4': '#4ADE80', // Verde
    'ESI-5': '#3B82F6', // Azul
  };
  return colores[nivelUrgencia] || '#757575';
};

// Componente para la fila expandible
const ExpandableRow = ({ caso }) => {
  const [open, setOpen] = useState(false);

  const fecha = caso.fechaCreacion ? 
    format(new Date(caso.fechaCreacion), "d 'de' MMMM, yyyy", { locale: es }) :
    'Fecha no disponible';

  const hora = caso.fechaCreacion ?
    format(new Date(caso.fechaCreacion), "HH:mm", { locale: es }) :
    '';

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{`${fecha} ${hora}`}</TableCell>
        <TableCell>
          {caso.tipo === 'audio' ? 'Análisis de Síntomas' : 
           caso.tipo === 'examen' ? 'Análisis de Examen' : 
           'Consulta'}
        </TableCell>
        <TableCell>
          <Chip 
            label={caso.nivelUrgencia || 'No especificado'} 
            size="small"
            sx={{ 
              bgcolor: getUrgencyColor(caso.nivelUrgencia || 'ESI-3'),
              color: 'white',
              fontWeight: 'bold'
            }}
          />
        </TableCell>
        <TableCell>{caso.estado || 'Pendiente'}</TableCell>
        <TableCell>{caso.diagnosticoPreliminar || 'No disponible'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <DetailSection
                        title="Diagnóstico Preliminar"
                        content={caso.diagnosticoPreliminar || 'No disponible'}
                        icon={<HospitalIcon color="primary" />}
                      />
                      <Divider sx={{ my: 2 }} />
                      <DetailSection
                        title="Síntomas"
                        content={caso.sintomas || []}
                        icon={<AssignmentIcon color="primary" />}
                      />
                      <Divider sx={{ my: 2 }} />
                      <DetailSection
                        title="Signos de Alarma"
                        content={caso.signosAlarma || []}
                        icon={<WarningIcon color="error" />}
                      />
                      <Divider sx={{ my: 2 }} />
                      <DetailSection
                        title="Recomendaciones"
                        content={caso.recomendaciones || []}
                        icon={<AssignmentIcon color="primary" />}
                      />
                      {caso.transcripcion && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <DetailSection
                            title="Transcripción"
                            content={caso.transcripcion}
                            icon={<AssignmentIcon color="primary" />}
                          />
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const ListaCasos = () => {
  const [casos, setCasos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const obtenerCasos = async () => {
      try {
        setLoading(true);
        setError(null);
        const usuario = JSON.parse(localStorage.getItem('usuario'));
        
        if (!usuario || !usuario._id) {
          throw new Error('No se encontró información del usuario');
        }

        console.log('Obteniendo casos para el usuario:', usuario._id);
        const response = await axiosInstance.get('/casos/paciente', {
          params: { pacienteId: usuario._id }
        });

        if (!response.data) {
          throw new Error('No se recibieron datos del servidor');
        }

        // Ordenar casos por fecha
        const casosOrdenados = response.data.sort((a, b) => {
          if (!a.fechaCreacion || !b.fechaCreacion) return 0;
          return new Date(b.fechaCreacion) - new Date(a.fechaCreacion);
        });

        console.log('Casos obtenidos:', casosOrdenados.length);
        setCasos(casosOrdenados);
      } catch (error) {
        console.error('Error al obtener casos:', error);
        setError(error.response?.data?.error || 'Error al cargar los casos. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    obtenerCasos();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!casos || casos.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Historial de Casos
          </Typography>
          <Alert severity="info">
            No tienes casos registrados aún.
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Historial de Casos
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Fecha</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Nivel de Urgencia</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Diagnóstico</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {casos.map((caso) => (
                <ExpandableRow key={caso._id} caso={caso} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  );
};

export default ListaCasos; 